import { useState } from 'react';
import { motion } from 'framer-motion';
import { Menu } from 'lucide-react';
import type { Language } from '@pullman/shared';
import { useTranslation } from '@/i18n';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { MobileMenu } from './MobileMenu';

const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'en', label: 'EN' },
  { code: 'hy', label: 'ՀԱ' },
  { code: 'ru', label: 'РУ' },
];

interface HeaderProps {
  collapsed?: boolean;
}

export function Header({ collapsed = false }: HeaderProps) {
  const { language, setLanguage } = useTranslation();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <motion.header
        className="fixed top-0 left-0 right-0 z-50 bg-brand-navy/95 backdrop-blur-sm border-b border-white/10"
        animate={{ height: collapsed && isMobile ? 48 : isMobile ? 56 : 64 }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
      >
        <div className="h-full max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-center relative">
          {/* Logo - centered */}
          <a href="/" className="flex items-center">
            <img
              src="https://cdn.prod.website-files.com/6685b27b98911d2b57296782/6685b27b98911d2b572967d1_Pullman_Logo.svg"
              alt="Pullman Residences"
              className={`${collapsed && isMobile ? 'h-5' : 'h-6 md:h-8'} w-auto brightness-0 invert transition-all`}
            />
          </a>

          {/* Language toggle + menu - absolute right */}
          <div className="absolute right-4 md:right-6 flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm">
              {LANGUAGES.map((lang, i) => (
                <span key={lang.code} className="flex items-center">
                  {i > 0 && <span className="text-white/30 mx-1">|</span>}
                  <button
                    onClick={() => setLanguage(lang.code)}
                    className={`px-1 py-0.5 transition-all ${
                      language === lang.code
                        ? 'text-brand-gold font-semibold'
                        : 'text-white/60 hover:text-white/90'
                    }`}
                    aria-label={`Switch to ${lang.label}`}
                  >
                    {lang.label}
                  </button>
                </span>
              ))}
            </div>

            {isMobile && (
              <button
                onClick={() => setMenuOpen(true)}
                className="p-2 text-white/80 hover:text-white transition-colors"
                aria-label="Open menu"
              >
                <Menu size={22} />
              </button>
            )}
          </div>
        </div>
      </motion.header>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
